import { useAuth } from '@agentnet/auth';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { GetFileInfoProps } from 'api/file/file-api';
import { downloadFincenReportXML, getFincenReportSummary, updateFincenReporStatusAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { FC, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AsyncStatusType } from '../constants';
import { PARTY_CATEGORIES } from '../report-party-information/constants';
import { useMenuContext } from '../ReportingLayout';
import { SummaryData, ValidationError } from '../types';
import SummaryContent from './SummaryContent';

const SummaryContainer: FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const history = useHistory();
  const { menuCollapsed } = useMenuContext();
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();

  const fileDataContext = useContext(FileDataContext) as FileDataContextInterface;
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { fincenReport } = fincenReportCtx ?? {};

  // Load file data when lost on refresh
  useEffect(() => {
    if (fileId && fileDataContext && !fileDataContext.fileData?.fileId && fileDataContext.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataContext.loadFileData(fileProps);
    }
  }, [fileId, fileDataContext]);

  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Redirect to files if fileId is not present (handles browser refresh edge case)
  if (!fileId) {
    return <Redirect to="/files" />;
  }

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      contentContainer: {
        paddingBottom: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
      },
      mainContainer: {
        width: '100%',
        position: 'relative',
      },
      contentWrapper: {
        overflow: 'hidden',
        paddingBottom: theme.spacing(8),
      },
    }),
  );

  const classes = useStyles();

  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [summaryStatus, setSummaryStatus] = useState<AsyncStatusType>('idle');
  const [summaryErrors, setSummaryErrors] = useState<ValidationError[]>([]);

  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        const reportId = String(fincenReport?.fincenReportId || 0);

        // Wait for fincenReport to be loaded before fetching summary
        if (!reportId || reportId === '0') {
          // Don't set error if fincenReport is still loading
          if (fincenReport === undefined || fincenReport === null) {
            return;
          }
          setSummaryStatus('error');
          setSummaryErrors([{ field: 'summary', message: 'Report ID is not available.' }]);
          return;
        }

        setSummaryStatus('pending');
        const token = await getAccessToken();
        const response = await getFincenReportSummary(reportId, token);

        // Map reportType value to name from PARTY_CATEGORIES
        const partyCategory = PARTY_CATEGORIES.find((category) => category.value === response.ReportType);
        const reportTypeName = partyCategory ? partyCategory.name : response.ReportType;

        // Map API response to SummaryData format
        const mappedData: SummaryData = {
          bsaIdentifier: response.BSAIdentifier,
          createdDate: response.CreatedDate,
          reportType: reportTypeName,
          reportName: response.ReportName,
          reportingPerson: response.ReportingPerson,
          property: response.Property.length > 1 ? response.Property : response.Property.join(', '),
          transferee: response.Transferee.length > 1 ? response.Transferee : response.Transferee.join(', '),
          transferor: response.Transferor.length > 1 ? response.Transferor : response.Transferor.join(', '),
          transaction: response.Transaction.length > 1 ? response.Transaction : response.Transaction.join(', '),
        };

        setSummaryData(mappedData);
        setSummaryStatus('success');
      } catch (error) {
        setSummaryStatus('error');
        setSummaryErrors([{ field: 'summary', message: 'Failed to fetch summary data.' }]);
      }
    };

    fetchSummaryData();
  }, [fileId, getAccessToken, fincenReport?.fincenReportId]);

  const handleDownloadReport = async () => {
    const reportId = String(fincenReport?.fincenReportId || 0);

    if (!reportId || reportId === '0') {
      addGlobalMsg({
        message: 'Report ID is not available. Please try again.',
        type: 'error',
      });
      return;
    }

    try {
      setDownloadingReport(true);
      addSnackbarMessage({
        message: 'Downloading FinCEN Report XML...',
        type: 'success',
      });

      const token = await getAccessToken();
      const response = await downloadFincenReportXML(reportId, token);

      const xmlBlob = await response.blob();
      const downloadUrl = URL.createObjectURL(xmlBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;

      const submissionType = 'RERXST';
      const fileId = fincenReport?.fincenReportId || fincenReport?.reportCertifier?.sdtmUserId;
      const fileFormat = 'XML';
      const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0].replace('T', '');
      downloadLink.download = `${submissionType}.${timestamp}.${fileId}.${fileFormat.toLowerCase()}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadUrl);
    } catch (err: any) {
      console.error(err);
      addGlobalMsg({
        message: err.message || 'Error downloading FinCEN report XML.',
        type: 'error',
      });
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleEditReport = () => {
    setShowEditDialog(true);
  };

  const handleConfirmEdit = async () => {
    setShowEditDialog(false);
    // Call PUT API with ReportStatusCdId 9300 - Open state when edit the Report
    try {
      const token = await getAccessToken();
      await updateFincenReporStatusAPI(
        fileId,
        {
          FincenReportId: fincenReport?.fincenReportId || 0,
          ReportStatusCdId: 9300,
        },
        token,
      );

      // Reload the fincen report to get updated status
      await fincenReportCtx?.reloadFincenReport?.(fileId);
    } catch (error) {
      console.error('Error updating report status:', error);
    }

    // Clear validation states before changing status to prevent showing error indicators
    if (fincenReportCtx?.setGenerateButtonClicked) {
      fincenReportCtx.setGenerateButtonClicked(false);
    }
    if (fincenReportCtx?.setAutoValidationTriggered) {
      fincenReportCtx.setAutoValidationTriggered(false);
    }
    fincenReportCtx?.clearReportingPartyValidationErrors?.();
    fincenReportCtx?.clearPropertyAddressValidationErrors?.();
    fincenReportCtx?.clearTransfereeValidationErrors?.();
    fincenReportCtx?.clearTransferorValidationErrors?.();
    fincenReportCtx?.clearPaymentValidationErrors?.();
    fincenReportCtx?.clearCertifierValidationErrors?.();
    fincenReportCtx?.setShowSummary?.(false);
    history.push(`/files/${fileId}/fincen-reporting/filing-information`);
  };

  const handleCancelEdit = () => {
    setShowEditDialog(false);
  };

  const handleBack = () => {
    history.push(`/files/${fileId}/fincen-reporting/certify-generate`);
  };

  return (
    <div data-testid="fincen-report-summary-container" className={classes.mainContainer}>
      <Grid container spacing={0}>
        <Grid item xs className={classes.contentWrapper}>
          <div className={classes.contentContainer}>
            <SummaryContent
              data={summaryData ?? undefined}
              status={summaryStatus}
              errors={summaryErrors}
              fileId={fileId}
              menuCollapsed={menuCollapsed}
              handleDownloadReport={handleDownloadReport}
              handleBack={handleBack}
              handleEditReport={handleEditReport}
            />
          </div>
        </Grid>
      </Grid>

      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBack}
        cancelDataQa="fincen-summary-back-button"
        secondaryActionLabel="Edit Report"
        onSecondaryClick={handleEditReport}
        secondaryActionDataQa="fincen-summary-edit-report-button"
        primaryActionLabel="Download Report"
        onPrimaryClick={handleDownloadReport}
        primaryActionDataQa="fincen-summary-download-report-button"
      />

      <AgentNetConfirmationDialog
        open={showEditDialog}
        onDismissAction={handleCancelEdit}
        onConfirm={handleConfirmEdit}
        dialogTitle="Edit Report"
        dialogText="Continuing will delete the existing XML file and reopen the report so you can make changes. You will need to select Generate Report again to create a new XML file."
        dialogCancelBtnContent="Cancel"
        dialogBtnContent="Continue Editing"
        qaAttrPrefix="fincen-edit-report"
        size="sm"
      />
    </div>
  );
};

export default SummaryContainer;
